import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Blocks,
  CalendarClock,
  Coins,
  Gift,
  Mail,
  ScrollText,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/shared/components/page-header';
import { useAuth } from '@/shared/lib/auth';
import { isMasterEmail } from '@/shared/lib/navigation';
import { AiCostsPanel } from '@/features/ai-credits';
import { CampaignAgendaPanel } from '@/features/campaign-agenda';
import { DispatchRunsPanel } from '@/features/dispatch-log';
import { formatDate, formatTime } from '@/shared/lib/format';
import { ApiError } from '@/shared/api/client';
import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/shared/ui/sheet';
import {
  fetchObservabilityIntegrations,
  fetchObservabilityRetention,
  updateObservabilityRetention,
} from '../api/use-observability';
import type { ObservabilityDirection, ObservabilityExchange, ObservabilityProvider, ObservabilityStatus } from '../model/types';
import { EmailsObservabilityTab } from './emails-tab';
import { LoyaltyTotaledPanel } from './loyalty-tab';
import { SecurityPanel } from './security-tab';

type Area = 'integrations' | 'emails' | 'campaigns' | 'dispatch' | 'ai' | 'loyalty' | 'security';

// Fidelidade lê o mesmo /observability/integrations (requireMaster) da aba Integrações.
const MASTER_AREAS: ReadonlySet<Area> = new Set<Area>(['integrations', 'emails', 'ai', 'loyalty', 'security']);

const AREA_TABS: Array<{ value: Area; label: string; icon: typeof Blocks }> = [
  { value: 'integrations', label: 'Integrações', icon: Blocks },
  { value: 'emails', label: 'E-mail e SMS', icon: Mail },
  { value: 'campaigns', label: 'Campanhas', icon: CalendarClock },
  { value: 'dispatch', label: 'Disparos', icon: ScrollText },
  { value: 'ai', label: 'IA', icon: Coins },
  { value: 'loyalty', label: 'Fidelidade', icon: Gift },
  { value: 'security', label: 'Segurança', icon: ShieldCheck },
];

/** `?aba=` aceita o id da aba ou o nome antigo das rotas (campanhas, disparos, ia). */
const AREA_ALIASES: Record<string, Area> = {
  integracoes: 'integrations',
  emails: 'emails',
  campanhas: 'campaigns',
  disparos: 'dispatch',
  ia: 'ai',
  fidelidade: 'loyalty',
  seguranca: 'security',
};

const PROVIDERS = [
  { value: 'all', label: 'Todos os sistemas' },
  { value: 'varejonline', label: 'Varejo Online' },
  { value: 'webengage', label: 'WebEngage' },
] as const;

const DIRECTIONS = [
  { value: 'all', label: 'Ida e volta' },
  { value: 'outbound', label: 'A gente chamou' },
  { value: 'inbound', label: 'Eles chamaram' },
] as const;

const STATUSES = [
  { value: 'all', label: 'Todos os status' },
  { value: 'success', label: 'Sucesso' },
  { value: 'failed', label: 'Falha' },
  { value: 'pending', label: 'Pendente' },
  { value: 'skipped', label: 'Ignorado' },
] as const;

function providerLabel(provider: ObservabilityProvider): string {
  if (provider === 'pdv3s') return 'PDV 3S';
  return provider === 'varejonline' ? 'Varejo Online' : 'WebEngage';
}

function directionLabel(direction: ObservabilityDirection, provider?: ObservabilityProvider): string {
  if (provider === 'varejonline') {
    return direction === 'outbound' ? 'Fixe → Varejo' : 'Varejo → Fixe';
  }
  return direction === 'outbound' ? 'A gente chamou' : 'Eles chamaram';
}

function statusMeta(status: ObservabilityStatus): { label: string; variant: BadgeProps['variant'] } {
  if (status === 'success') return { label: 'Sucesso', variant: 'success' };
  if (status === 'failed') return { label: 'Falha', variant: 'destructive' };
  if (status === 'pending') return { label: 'Pendente', variant: 'warning' };
  return { label: 'Ignorado', variant: 'secondary' };
}

function pretty(value: unknown): string {
  if (value == null) return '';
  return JSON.stringify(value, null, 2);
}

function PayloadBlock({ title, value, empty }: { title: string; value: unknown; empty: string }) {
  const body = pretty(value);
  return (
    <div className="min-w-0">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      {body ? (
        <pre className="max-h-80 overflow-auto rounded-xl border border-border/60 bg-muted/40 p-3 text-xs leading-relaxed">
          {body}
        </pre>
      ) : (
        <p className="rounded-xl border border-dashed border-border/70 px-3 py-6 text-center text-sm text-muted-foreground">
          {empty}
        </p>
      )}
    </div>
  );
}

function ExchangeSummary({ row }: { row: ObservabilityExchange }) {
  const meta = statusMeta(row.status);
  const DirectionIcon = row.direction === 'outbound' ? ArrowUpRight : ArrowDownLeft;
  return (
    <>
      <div className="min-w-0">
        <p className="truncate font-medium">{row.personName}</p>
        <p className="truncate text-xs text-muted-foreground">
          {row.personDocumentMasked} · {row.storeName}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="outline">{providerLabel(row.provider)}</Badge>
        <Badge variant="info">
          <DirectionIcon className="size-3" />
          {directionLabel(row.direction, row.provider)}
        </Badge>
        <Badge variant="secondary">{row.event}</Badge>
      </div>
      <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span className="tabular-nums">
          {formatDate(row.at)} {formatTime(row.at)}
        </span>
        <span className="flex items-center gap-2">
          <span className="tabular-nums">{row.httpStatus ?? '—'}</span>
          <Badge variant={meta.variant}>{meta.label}</Badge>
        </span>
      </div>
    </>
  );
}

function RetentionCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['observability-retention'],
    queryFn: fetchObservabilityRetention,
  });
  const [enabled, setEnabled] = useState(true);
  const [days, setDays] = useState('15');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!query.data) return;
    setEnabled(query.data.enabled);
    setDays(String(query.data.intervalDays));
  }, [query.data]);

  const onSave = async () => {
    const intervalDays = Number(days);
    if (!Number.isInteger(intervalDays) || intervalDays < 1 || intervalDays > 365) {
      toast.error('Informe um intervalo entre 1 e 365 dias.');
      return;
    }
    setSaving(true);
    try {
      await updateObservabilityRetention({ enabled, intervalDays });
      await queryClient.invalidateQueries({ queryKey: ['observability-retention'] });
      toast.success(enabled ? `Limpeza a cada ${intervalDays} dias, à meia-noite.` : 'Limpeza automática desligada.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar a limpeza.');
    } finally {
      setSaving(false);
    }
  };

  const last = query.data?.lastRunAt;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base">Limpeza dos logs</CardTitle>
        <CardDescription>
          Na meia-noite, no intervalo abaixo, apaga as trocas mais antigas que esse prazo. A fila de envio fica.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border/50 px-3 py-2">
          <Label htmlFor="observability-purge" className="cursor-pointer">
            Limpeza automática
          </Label>
          <Switch id="observability-purge" checked={enabled} onCheckedChange={setEnabled} />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 sm:w-40">
            <Label htmlFor="observability-days">A cada (dias)</Label>
            <Input
              id="observability-days"
              className="mt-1.5"
              inputMode="numeric"
              value={days}
              onChange={(event) => setDays(event.target.value.replace(/\D/g, '').slice(0, 3))}
            />
          </div>
          <Button onClick={() => void onSave()} loading={saving} disabled={query.isLoading}>
            Salvar limpeza
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {last
            ? `Última limpeza em ${formatDate(last)} ${formatTime(last)}. ${query.data?.lastDeleted ?? 0} troca(s) apagada(s).`
            : 'Ainda não rodou. A primeira meia-noite faz a limpeza.'}
        </p>
      </CardContent>
    </Card>
  );
}

function readArea(value: string | null, allowed: (area: Area) => boolean): Area {
  const area = value ? (AREA_ALIASES[value] ?? AREA_TABS.find((tab) => tab.value === value)?.value) : undefined;
  if (area && allowed(area)) return area;
  return allowed('integrations') ? 'integrations' : 'campaigns';
}

/**
 * Observabilidade do master: integrações, e-mail/SMS, agenda de campanhas,
 * log de disparos, custos de IA e (depois) fidelidade — tudo em abas.
 * Admin comum vê só Campanhas e Disparos; o resto é do master.
 */
export function ObservabilityPage() {
  const { user } = useAuth();
  const master = isMasterEmail(user?.email);
  const allowed = (area: Area) => master || !MASTER_AREAS.has(area);
  const [params, setParams] = useSearchParams();
  const area = readArea(params.get('aba'), allowed);
  const [provider, setProvider] = useState('all');
  const [direction, setDirection] = useState('all');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const setArea = (next: Area) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'integrations') nextParams.delete('aba');
    else nextParams.set('aba', next);
    if (next !== 'emails') nextParams.delete('evento');
    setParams(nextParams, { replace: true });
    setOpenId(null);
  };

  const query = useQuery({
    queryKey: ['observability-integrations', provider, direction, status, q],
    queryFn: () => fetchObservabilityIntegrations({ provider, direction, status, q }),
    enabled: area === 'integrations',
  });

  const rows = query.data?.rows ?? [];
  const counts = query.data?.counts;
  const selected = rows.find((row) => row.id === openId) ?? null;

  if (!user || user.role !== 'Admin') return <Navigate to="/login" replace />;

  return (
    <>
      <PageHeader
        title="Observabilidade"
      />

      <SegmentedControl
        value={area}
        onChange={setArea}
        className="mb-4"
        options={AREA_TABS.filter((tab) => allowed(tab.value))}
      />

      {area === 'loyalty' ? (
        <LoyaltyTotaledPanel />
      ) : area === 'security' ? (
        <SecurityPanel />
      ) : area === 'emails' ? (
        <EmailsObservabilityTab />
      ) : area === 'campaigns' ? (
        <CampaignAgendaPanel />
      ) : area === 'dispatch' ? (
        <DispatchRunsPanel />
      ) : area === 'ai' ? (
        <AiCostsPanel />
      ) : (
        <>
          <RetentionCard />
          <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-3">
            {(
              [
                { key: 'success', label: 'Sucesso', value: counts?.success ?? 0 },
                { key: 'failed', label: 'Falha', value: counts?.failed ?? 0 },
                { key: 'pending', label: 'Pendente', value: counts?.pending ?? 0 },
              ] as const
            ).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setStatus((current) => (current === item.key ? 'all' : item.key))}
                className="text-left"
              >
                <Card className={`p-3 sm:p-4 ${status === item.key ? 'ring-2 ring-primary' : ''}`}>
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p
                    className={`mt-1 text-xl font-semibold tabular-nums sm:text-2xl ${
                      item.key === 'failed' && item.value > 0 ? 'text-destructive' : ''
                    }`}
                  >
                    {query.isLoading ? '—' : item.value.toLocaleString('pt-BR')}
                  </p>
                </Card>
              </button>
            ))}
          </div>

          <div className="mb-4 grid gap-2 sm:flex sm:flex-wrap sm:items-center">
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="w-full sm:w-auto sm:min-w-[11rem]">
                <SelectValue placeholder="Sistema" />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="w-full sm:w-auto sm:min-w-[9rem]">
                <SelectValue placeholder="Direção" />
              </SelectTrigger>
              <SelectContent>
                {DIRECTIONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full sm:w-auto sm:min-w-[10rem]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative min-w-0 sm:w-56">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder="Buscar por nome"
                className="pl-9"
                aria-label="Buscar por nome"
              />
            </div>
          </div>

          <div className="glass overflow-hidden rounded-2xl">
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="whitespace-nowrap px-4 py-3 font-medium">Quando</th>
                    <th className="px-4 py-3 font-medium">Pessoa</th>
                    <th className="px-4 py-3 font-medium">Sistema</th>
                    <th className="px-4 py-3 font-medium">Direção</th>
                    <th className="px-4 py-3 font-medium">Evento</th>
                    <th className="px-4 py-3 font-medium">HTTP</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {query.isLoading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                        Carregando…
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                        Nenhuma troca no filtro selecionado.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const meta = statusMeta(row.status);
                      return (
                        <tr
                          key={row.id}
                          onClick={() => setOpenId(row.id)}
                          className="cursor-pointer border-b border-border/40 transition-colors last:border-0 hover:bg-muted/40"
                        >
                          <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                            {formatDate(row.at)} {formatTime(row.at)}
                          </td>
                          <td className="px-4 py-3">
                            <p>{row.personName}</p>
                            <p className="text-xs text-muted-foreground">{row.personDocumentMasked}</p>
                          </td>
                          <td className="px-4 py-3">{providerLabel(row.provider)}</td>
                          <td className="px-4 py-3">{directionLabel(row.direction, row.provider)}</td>
                          <td className="px-4 py-3">{row.event}</td>
                          <td className="px-4 py-3 tabular-nums">{row.httpStatus ?? '—'}</td>
                          <td className="px-4 py-3">
                            <Badge variant={meta.variant}>{meta.label}</Badge>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden">
              {query.isLoading ? (
                <p className="px-4 py-10 text-center text-sm text-muted-foreground">Carregando…</p>
              ) : rows.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                  Nenhuma troca no filtro selecionado.
                </p>
              ) : (
                <ul>
                  {rows.map((row) => (
                    <li key={row.id} className="border-b border-border/40 last:border-0">
                      <button
                        type="button"
                        onClick={() => setOpenId(row.id)}
                        className="flex w-full flex-col gap-2 px-4 py-3 text-left hover:bg-muted/40"
                      >
                        <ExchangeSummary row={row} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      <Sheet open={Boolean(selected)} onOpenChange={(open) => !open && setOpenId(null)}>
        <SheetContent className="w-full max-w-none sm:w-[min(52rem,calc(100%-2rem))]">
          {selected ? (
            <>
              <SheetHeader>
                <SheetTitle>{selected.personName}</SheetTitle>
                <SheetDescription>
                  {providerLabel(selected.provider)} · {directionLabel(selected.direction, selected.provider)} · {selected.event}
                  {' · '}
                  {selected.storeName}
                </SheetDescription>
              </SheetHeader>
              {selected.note ? <p className="mb-4 text-sm text-muted-foreground">{selected.note}</p> : null}
              <div className="grid gap-4 md:grid-cols-2">
                <PayloadBlock
                  title={
                    selected.provider === 'varejonline' && selected.direction === 'inbound'
                      ? 'O que a Fixe respondeu'
                      : 'O que a Fixe enviou'
                  }
                  value={selected.sent}
                  empty="Nada enviado."
                />
                <PayloadBlock
                  title={
                    selected.provider === 'varejonline' && selected.direction === 'inbound'
                      ? 'O que a Varejo mandou'
                      : 'O que voltou'
                  }
                  value={selected.received}
                  empty="Nada recebido."
                />
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
